import type { Session } from '@obraplan/core/application/session';
import type { AppError } from '@obraplan/core/application/errors/app-error';
import type { Result } from '@obraplan/core/domain/shared/result';
import type { Clock } from '@obraplan/core/application/ports/clock';
import type { StorageInspector } from '@obraplan/core/application/ports/storage-inspector';
import { PermissionChecker } from '@obraplan/core/application/services/permission-checker';
import {
  ClearSessionToken,
  GetSessionToken,
  SetSessionToken,
} from '@obraplan/core/application/use-cases/access/session-token';
import {
  GetActiveProjectId,
  SetActiveProject,
  type CreateProject,
  type GetProject,
  type UpdateProject,
} from '@obraplan/core/application/use-cases/projects/project-use-cases';
import {
  GetStorageStatus,
  RequestStoragePersistence,
  type GetDefaultWorkCalendar,
  type ListAccessibleProjects,
  type ListPermissions,
  type ListRecentAudit,
  type ListRoles,
} from '@obraplan/core/application/use-cases/system/system-queries';
import { SystemClock } from '@/infrastructure/clock/system-clock';
import { getDb, type ObraPlanDb } from '@/infrastructure/persistence/db';
import type { ConnectionStatus } from '@/infrastructure/http/connection-status';
import type { Outbox } from './outbox-types';
import { DexieSettingsRepository } from '@/infrastructure/persistence/settings.repository';
import { BrowserStorageInspector } from '@/infrastructure/storage/browser-storage-inspector';
import type { ListUsers } from '@obraplan/core/application/use-cases/access/list-users';
import type { CreateUser } from '@obraplan/core/application/use-cases/access/create-user';
import type { UpdateUser } from '@obraplan/core/application/use-cases/access/update-user';
import type { UpdateMyProfile } from '@obraplan/core/application/use-cases/access/update-my-profile';
import type { GetMySession } from '@obraplan/core/application/use-cases/access/get-my-session';
import type { ListSupplyProjects } from '@obraplan/core/application/use-cases/projects/list-supply-projects';
import type { SearchSupplyProducts } from '@obraplan/core/application/use-cases/takeoff/search-supply-products';
import type { GetSupplyAveragePrices } from '@obraplan/core/application/use-cases/takeoff/get-supply-average-prices';
import type {
  GetProductUnitFactors,
  GetProjectMaterialPrices,
  RefreshMaterialPrices,
  SetMaterialPrice,
  SetProductUnitFactors,
} from '@obraplan/core/application/use-cases/takeoff/material-price-use-cases';
import type {
  GetProjectLaborRates,
  SetLaborProvisionalRate,
} from '@obraplan/core/application/use-cases/takeoff/labor-rate-use-cases';
import type {
  ApproveProjectBudget,
  GetProjectApprovedBudget,
} from '@obraplan/core/application/use-cases/takeoff/project-budget-use-cases';
import type {
  AssignRole,
  RevokeRole,
} from '@obraplan/core/application/use-cases/access/assign-role';
import type { TransferSuperAdmin } from '@obraplan/core/application/use-cases/access/transfer-super-admin';
import type {
  CreateRole,
  DeleteRole,
  UpdateRole,
} from '@obraplan/core/application/use-cases/access/role-editing';
import type {
  ApproveProjectVersion,
  PreviewProjectVersionApproval,
  DeleteProjectVersion,
  DuplicateProject,
} from '@obraplan/core/application/use-cases/projects/project-version-use-cases';
import type { TransferServicesToApproved } from '@obraplan/core/application/use-cases/projects/service-transfer-use-cases';
import type {
  GetProjectOutline,
  GetProjectStructure,
} from '@obraplan/core/application/use-cases/structure/get-project-structure';
import type {
  ApplyDisciplinesToFloors,
  ChangeActivityStatus,
  CreateActivity,
  CreateDiscipline,
  CreateFloor,
  CreateFloorRange,
  CreateTower,
  ListUnitsOfMeasure,
  RenameFloor,
  RenameTower,
  UpdateActivitySchedule,
} from '@obraplan/core/application/use-cases/structure/structure-use-cases';
import type {
  ConfirmDisciplineImport,
  ListDisciplineCatalog,
  PreviewDisciplineImport,
  SetDisciplineActive,
  UpdateDiscipline,
} from '@obraplan/core/application/use-cases/structure/discipline-catalog-use-cases';
import type {
  DeleteStructureNode,
  PreviewStructureNodeImpact,
} from '@obraplan/core/application/use-cases/structure/delete-structure-node';
import type {
  CreateFloorPlan,
  CreateUnitType,
  DeleteFloorPlan,
  DeleteUnitType,
  GetProjectTakeoff,
  LinkFloorToFloorPlan,
  RenameFloorPlan,
  RenameUnitType,
} from '@obraplan/core/application/use-cases/takeoff/takeoff-use-cases';
import type { SetUnitTypeServices } from '@obraplan/core/application/use-cases/takeoff/unit-type-services-use-cases';
import type {
  GetProjectGeometry,
  SaveProjectGeometry,
} from '@obraplan/core/application/use-cases/takeoff/geometry-use-cases';
import type {
  GetFrontPieces,
  RecordPieceExecutions,
} from '@obraplan/core/application/use-cases/takeoff/piece-execution-use-cases';
import type {
  GetFrontRooms,
  RecordRoomProgress,
  SetRoomNotApplicable,
} from '@obraplan/core/application/use-cases/takeoff/room-progress-use-cases';
import type {
  ClearImportedPiecePosition,
  GetImportedPieces,
  SetImportedPiecePosition,
  RenameImportedPiece,
} from '@obraplan/core/application/use-cases/takeoff/imported-piece-use-cases';
import type {
  AttachFloorPlanSheet,
  CalibrateFloorPlanSheet,
  GetFloorPlanSheetFile,
  GetProjectFloorPlanSheets,
  RemoveFloorPlanSheet,
} from '@obraplan/core/application/use-cases/takeoff/floor-plan-sheet-use-cases';
import type {
  ApplyServiceCatalog,
  GetProjectServices,
} from '@obraplan/core/application/use-cases/takeoff/service-catalog-use-cases';
import type {
  GetProjectServiceItems,
  SaveProjectServiceItems,
} from '@obraplan/core/application/use-cases/takeoff/service-items-use-cases';
import type {
  CreateGlobalService,
  ListGlobalServices,
  SetGlobalServiceActive,
  UpdateGlobalService,
} from '@obraplan/core/application/use-cases/takeoff/global-service-use-cases';
import type { SetProjectServiceUnit } from '@obraplan/core/application/use-cases/takeoff/service-unit-use-cases';
import type { GetProductivitySuggestion } from '@obraplan/core/application/use-cases/takeoff/productivity-use-cases';
import type {
  GetApartmentAverage,
  GetPileTypeAverage,
  GetRateAverages,
  SaveTakeoffMeasures,
  SetCatalogReferenceProject,
  SetProjectInRateAverage,
} from '@obraplan/core/application/use-cases/takeoff/rate-average-use-cases';
import type {
  ListServiceChecklists,
  SaveServiceChecklist,
  SetServiceChecklistActive,
} from '@obraplan/core/application/use-cases/quality/checklist-use-cases';
import type { GetDayQuality } from '@obraplan/core/application/use-cases/quality/quality-use-cases';
import type {
  CancelNonConformity,
  ListProjectNonConformities,
  ResolveNonConformity,
} from '@obraplan/core/application/use-cases/quality/non-conformity-use-cases';
import type {
  GetServiceQualitySheet,
  ListQualitySheetServices,
} from '@obraplan/core/application/use-cases/quality/quality-sheet-use-cases';
import type {
  AddActivityItem,
  CreateMaterial,
  GetCatalog,
  LinkMaterialToSku,
  UpdateActivityItem,
} from '@obraplan/core/application/use-cases/schedule/schedule-use-cases';
import type {
  ConfirmMaterialImport,
  ListMaterials,
  PreviewMaterialImport,
} from '@obraplan/core/application/use-cases/schedule/material-import-use-cases';
import type {
  CancelScheduleImport,
  ConfirmScheduleImport,
  ListScheduleImports,
  StartScheduleImport,
  ValidateScheduleImport,
} from '@obraplan/core/application/use-cases/schedule/schedule-import-use-cases';
import type { GetMaterialAlerts } from '@obraplan/core/application/use-cases/schedule/get-material-alerts';
import type {
  ConfirmScheduleItemsImport,
  ListScheduleItems,
  PreviewScheduleItemsImport,
  UpdateScheduleItem,
} from '@obraplan/core/application/use-cases/schedule/schedule-item-use-cases';
import type {
  ApplyScheduleTakeoffRefresh,
  ConfirmScheduleFromTakeoff,
  GetScheduleTakeoffDrift,
  PreviewScheduleFromTakeoff,
  PreviewScheduleTakeoffRefresh,
} from '@obraplan/core/application/use-cases/schedule/schedule-from-takeoff-use-cases';
import type {
  ConfirmPurchaseLinesImport,
  ListPurchaseLines,
  PreviewPurchaseLinesImport,
} from '@obraplan/core/application/use-cases/schedule/purchase-line-use-cases';
import type {
  ConfirmPurchaseLinesFromTakeoff,
  PreviewPurchaseLinesFromTakeoff,
} from '@obraplan/core/application/use-cases/schedule/purchase-from-takeoff-use-cases';
import type {
  ConfirmLaborImport,
  ListItemLabor,
  PreviewLaborImport,
} from '@obraplan/core/application/use-cases/schedule/item-labor-use-cases';
import type { GetDashboard } from '@obraplan/core/application/use-cases/reports/dashboard-use-cases';
import type {
  AddCalendarException,
  ChangeEmployeeStatus,
  CreateEmployee,
  CreateExternalService,
  CreateJobRole,
  CreateTeam,
  GetResourceCapacity,
  GetSystemCalendar,
  GetWorkCalendar,
  GetWorkforce,
  ImportCalendarExceptions,
  MoveEmployeeToTeam,
  RemoveCalendarException,
  SetJobRoleDisciplines,
  UpdateEmployee,
  UpdateExternalService,
  UpdateTeam,
} from '@obraplan/core/application/use-cases/workforce/workforce-use-cases';
import type { SetTeamDisciplines } from '@obraplan/core/application/use-cases/workforce/team-discipline-use-cases';
import type {
  ConfirmEmployeeImport,
  PreviewEmployeeImport,
} from '@obraplan/core/application/use-cases/workforce/employee-import-use-cases';
import type {
  ConfirmStructureImport,
  PreviewStructureImport,
} from '@obraplan/core/application/use-cases/structure/structure-import-use-cases';
import type {
  GetResourceAvailability,
  GetWeeklyBoard,
  SuggestWeeklyDistribution,
} from '@obraplan/core/application/use-cases/planning/weekly-board-queries';
import type {
  ChangeWeeklyPlanStatus,
  CopyPreviousWeek,
  CreateWeeklyAllocation,
  DeleteWeeklyAllocation,
  GetOrCreateWeeklyPlan,
  UpdateWeeklyAllocation,
} from '@obraplan/core/application/use-cases/planning/planning-use-cases';
import type {
  ApplyScheduleAllocation,
  SuggestScheduleAllocation,
} from '@obraplan/core/application/use-cases/planning/auto-allocation-use-cases';
import type {
  GetDailySchedule,
  MoveAllocationToDay,
} from '@obraplan/core/application/use-cases/planning/daily-plan-use-cases';
import type {
  AdjustInspectionAllocation,
  ConfirmInspection,
  GetDailyInspections,
  GetPlanningChangeHistory,
  OpenInspection,
  RecordDayMeasurement,
  RecordReportedWork,
  RegisterInspectionAbsence,
  ReopenInspection,
} from '@obraplan/core/application/use-cases/planning/inspection-use-cases';
import type { RemoveInspectionAllocation } from '@obraplan/core/application/use-cases/planning/remove-inspection-allocation';
import type {
  CloseDailyReport,
  GetDailyReport,
  GetDailyReportCrew,
  GetDailyReportTasks,
  GetOrOpenDailyReport,
  ReviseDailyReport,
  SetDailyReportCrew,
  SetDailyReportEquipment,
  SetDailyReportNotes,
  SetDailyReportTasks,
  SetDailyReportWeather,
} from '@obraplan/core/application/use-cases/planning/daily-report-use-cases';
import type {
  GetDayTeamMeasurements,
  RecordReportedTeamWork,
  RecordTeamMeasurement,
} from '@obraplan/core/application/use-cases/planning/team-measurement-use-cases';
import type { CreateTeamAllocation } from '@obraplan/core/application/use-cases/planning/team-planning-use-cases';
import type {
  AttachDailyReportPhoto,
  AttachInspectionFile,
  AttachNonConformityPhoto,
  AttachTeamMeasurementPhoto,
  GetAttachmentUrl,
  ListDailyReportPhotos,
  ListInspectionAttachments,
  ListNonConformityPhotos,
  ListTeamMeasurementPhotos,
  RemoveInspectionAttachment,
} from '@obraplan/core/application/use-cases/planning/attachment-use-cases';
import type {
  RepairIntegrity,
  RunIntegrityCheck,
} from '@obraplan/core/application/use-cases/system/integrity-use-cases';
import type {
  AcknowledgeSupplyMaterial,
  GetSupplyDigest,
} from '@obraplan/core/application/use-cases/schedule/supply-use-cases';
import type { ListProjectRequisitions } from '@obraplan/core/application/use-cases/schedule/requisition-use-cases';
import type {
  RevokeUserSessions,
  SetPassword,
  SignInWithPassword,
  SignOut,
} from '@obraplan/core/application/use-cases/access/authentication';
import type {
  CreateApiKey,
  ListApiKeys,
  RevokeApiKey,
} from '@obraplan/core/application/use-cases/access/api-keys';

/**
 * O que a tela alcança: os casos de uso e os poucos serviços do aparelho.
 *
 * Os tipos são as classes do núcleo, e é de propósito: a tela chama
 * `container.getProject.execute(projectId, session)` com a assinatura que o caso de uso
 * declara, e o compilador confere cada chamada. Quem os executa é o servidor — o
 * `createRemoteContainer` monta cada um como requisição —, e o `endpoint-catalog.test.ts`
 * confere, contra o servidor de verdade, que as chaves existem e as formas batem.
 *
 * Até a Fase 32 havia um segundo jeito de montar tudo isto, sobre o IndexedDB do navegador,
 * e era sobre ele que o remoto se apoiava. Saiu: do navegador ficaram só o token da sessão,
 * a obra aberta e a cota (`createDeviceContainer`, abaixo).
 */

/** De um token para a sessão de quem o porta. Quem responde é o servidor (`GetMySession`). */
export interface SessionResolver {
  resolve(token: string): Promise<Result<Session, AppError>>;
}

export interface Container {
  readonly bootstrap: () => Promise<void>;
  /** Resolve o token guardado no aparelho na sessão de quem opera. */
  readonly sessions: SessionResolver;
  /**
   * Se o servidor está respondendo, e de quando é o dado guardado que a tela mostra (Fase 43,
   * RN-599). Quem escreve é o transporte; a interface só lê, pelo indicador da barra.
   */
  readonly connection: ConnectionStatus;
  /** A fila da Conferência feita sem sinal (Fase 43, etapa 2, RN-603 a RN-610). */
  readonly outbox: Outbox;

  readonly listUsers: ListUsers;
  readonly createUser: CreateUser;
  readonly updateUser: UpdateUser;
  readonly updateMyProfile: UpdateMyProfile;
  readonly getMySession: GetMySession;
  readonly listSupplyProjects: ListSupplyProjects;
  readonly searchSupplyProducts: SearchSupplyProducts;
  /** O preço médio do compras (Fase 29). */
  readonly getSupplyAveragePrices: GetSupplyAveragePrices;
  /** O preço do material na obra (Fase 29, etapa 2; a `0065`). */
  readonly getProjectMaterialPrices: GetProjectMaterialPrices;
  readonly setMaterialPrice: SetMaterialPrice;
  readonly refreshMaterialPrices: RefreshMaterialPrices;
  /** O fator de unidade do produto, para todas as obras (13/10/2026). */
  readonly getProductUnitFactors: GetProductUnitFactors;
  readonly setProductUnitFactors: SetProductUnitFactors;
  /** O custo da mão de obra no orçamento (Fase 29, etapa 3). */
  readonly getProjectLaborRates: GetProjectLaborRates;
  readonly setLaborProvisionalRate: SetLaborProvisionalRate;
  /** O orçamento aprovado (Fase 29, etapa 4; a `0067`). */
  readonly approveProjectBudget: ApproveProjectBudget;
  readonly getProjectApprovedBudget: GetProjectApprovedBudget;
  readonly assignRole: AssignRole;
  readonly revokeRole: RevokeRole;
  readonly transferSuperAdmin: TransferSuperAdmin;
  readonly createRole: CreateRole;
  readonly updateRole: UpdateRole;
  readonly deleteRole: DeleteRole;

  readonly listRoles: ListRoles;
  readonly listPermissions: ListPermissions;
  readonly listAccessibleProjects: ListAccessibleProjects;
  readonly getDefaultWorkCalendar: GetDefaultWorkCalendar;
  readonly listRecentAudit: ListRecentAudit;

  readonly createProject: CreateProject;
  readonly updateProject: UpdateProject;
  readonly duplicateProject: DuplicateProject;
  readonly deleteProjectVersion: DeleteProjectVersion;
  readonly approveProjectVersion: ApproveProjectVersion;
  readonly previewProjectVersionApproval: PreviewProjectVersionApproval;
  /** O serviço que vai da versão para a obra aprovada (Fase 38, RN-487). */
  readonly transferServicesToApproved: TransferServicesToApproved;
  readonly getProject: GetProject;
  readonly setActiveProject: SetActiveProject;
  readonly getActiveProjectId: GetActiveProjectId;

  readonly getProjectStructure: GetProjectStructure;
  readonly getProjectOutline: GetProjectOutline;
  readonly createTower: CreateTower;
  readonly renameTower: RenameTower;
  readonly createFloor: CreateFloor;
  readonly createFloorRange: CreateFloorRange;
  readonly renameFloor: RenameFloor;
  readonly createDiscipline: CreateDiscipline;
  readonly listDisciplineCatalog: ListDisciplineCatalog;
  readonly updateDiscipline: UpdateDiscipline;
  readonly setDisciplineActive: SetDisciplineActive;
  readonly previewDisciplineImport: PreviewDisciplineImport;
  readonly confirmDisciplineImport: ConfirmDisciplineImport;
  readonly createActivity: CreateActivity;
  readonly applyDisciplinesToFloors: ApplyDisciplinesToFloors;
  readonly changeActivityStatus: ChangeActivityStatus;
  readonly listUnitsOfMeasure: ListUnitsOfMeasure;
  readonly updateActivitySchedule: UpdateActivitySchedule;
  readonly deleteStructureNode: DeleteStructureNode;
  readonly previewStructureNodeImpact: PreviewStructureNodeImpact;

  readonly getProjectTakeoff: GetProjectTakeoff;
  readonly setUnitTypeServices: SetUnitTypeServices;
  readonly getProjectGeometry: GetProjectGeometry;
  readonly saveProjectGeometry: SaveProjectGeometry;
  readonly getFrontPieces: GetFrontPieces;
  readonly getImportedPieces: GetImportedPieces;
  readonly setImportedPiecePosition: SetImportedPiecePosition;
  readonly renameImportedPiece: RenameImportedPiece;
  readonly clearImportedPiecePosition: ClearImportedPiecePosition;
  readonly recordPieceExecutions: RecordPieceExecutions;
  readonly getFrontRooms: GetFrontRooms;
  readonly recordRoomProgress: RecordRoomProgress;
  readonly setRoomNotApplicable: SetRoomNotApplicable;
  readonly getProjectFloorPlanSheets: GetProjectFloorPlanSheets;
  readonly getFloorPlanSheetFile: GetFloorPlanSheetFile;
  readonly attachFloorPlanSheet: AttachFloorPlanSheet;
  readonly calibrateFloorPlanSheet: CalibrateFloorPlanSheet;
  readonly removeFloorPlanSheet: RemoveFloorPlanSheet;
  readonly getProjectServices: GetProjectServices;
  readonly applyServiceCatalog: ApplyServiceCatalog;
  readonly getProjectServiceItems: GetProjectServiceItems;
  readonly saveProjectServiceItems: SaveProjectServiceItems;
  readonly listGlobalServices: ListGlobalServices;
  readonly createGlobalService: CreateGlobalService;
  readonly updateGlobalService: UpdateGlobalService;
  readonly setGlobalServiceActive: SetGlobalServiceActive;
  readonly setProjectServiceUnit: SetProjectServiceUnit;
  readonly getProductivitySuggestion: GetProductivitySuggestion;
  readonly getRateAverages: GetRateAverages;
  readonly getPileTypeAverage: GetPileTypeAverage;
  readonly getApartmentAverage: GetApartmentAverage;
  readonly saveTakeoffMeasures: SaveTakeoffMeasures;
  readonly setProjectInRateAverage: SetProjectInRateAverage;
  readonly setCatalogReferenceProject: SetCatalogReferenceProject;

  readonly listServiceChecklists: ListServiceChecklists;
  readonly saveServiceChecklist: SaveServiceChecklist;
  readonly setServiceChecklistActive: SetServiceChecklistActive;
  readonly getDayQuality: GetDayQuality;
  /** A aba Não conformidades (Fase 37, RN-481 a RN-484). */
  readonly listProjectNonConformities: ListProjectNonConformities;
  readonly resolveNonConformity: ResolveNonConformity;
  readonly cancelNonConformity: CancelNonConformity;
  readonly listQualitySheetServices: ListQualitySheetServices;
  readonly getServiceQualitySheet: GetServiceQualitySheet;
  readonly createFloorPlan: CreateFloorPlan;
  readonly renameFloorPlan: RenameFloorPlan;
  readonly deleteFloorPlan: DeleteFloorPlan;
  readonly createUnitType: CreateUnitType;
  readonly renameUnitType: RenameUnitType;
  readonly deleteUnitType: DeleteUnitType;
  readonly linkFloorToFloorPlan: LinkFloorToFloorPlan;

  readonly getCatalog: GetCatalog;
  readonly listMaterials: ListMaterials;
  readonly createMaterial: CreateMaterial;
  readonly linkMaterialToSku: LinkMaterialToSku;
  readonly previewMaterialImport: PreviewMaterialImport;
  readonly confirmMaterialImport: ConfirmMaterialImport;

  readonly startScheduleImport: StartScheduleImport;
  readonly validateScheduleImport: ValidateScheduleImport;
  readonly confirmScheduleImport: ConfirmScheduleImport;
  readonly cancelScheduleImport: CancelScheduleImport;
  readonly listScheduleImports: ListScheduleImports;
  readonly getMaterialAlerts: GetMaterialAlerts;
  readonly listScheduleItems: ListScheduleItems;
  readonly previewScheduleItemsImport: PreviewScheduleItemsImport;
  readonly confirmScheduleItemsImport: ConfirmScheduleItemsImport;
  readonly updateScheduleItem: UpdateScheduleItem;
  readonly previewScheduleFromTakeoff: PreviewScheduleFromTakeoff;
  readonly confirmScheduleFromTakeoff: ConfirmScheduleFromTakeoff;
  readonly getScheduleTakeoffDrift: GetScheduleTakeoffDrift;
  readonly previewScheduleTakeoffRefresh: PreviewScheduleTakeoffRefresh;
  readonly applyScheduleTakeoffRefresh: ApplyScheduleTakeoffRefresh;
  readonly listPurchaseLines: ListPurchaseLines;
  readonly previewPurchaseLinesImport: PreviewPurchaseLinesImport;
  readonly confirmPurchaseLinesImport: ConfirmPurchaseLinesImport;
  readonly previewPurchaseLinesFromTakeoff: PreviewPurchaseLinesFromTakeoff;
  readonly confirmPurchaseLinesFromTakeoff: ConfirmPurchaseLinesFromTakeoff;
  readonly previewLaborImport: PreviewLaborImport;
  readonly confirmLaborImport: ConfirmLaborImport;
  readonly listItemLabor: ListItemLabor;

  readonly getDashboard: GetDashboard;

  readonly getWorkforce: GetWorkforce;
  readonly createJobRole: CreateJobRole;
  readonly setJobRoleDisciplines: SetJobRoleDisciplines;
  readonly setTeamDisciplines: SetTeamDisciplines;
  readonly previewEmployeeImport: PreviewEmployeeImport;
  readonly confirmEmployeeImport: ConfirmEmployeeImport;
  readonly previewStructureImport: PreviewStructureImport;
  readonly confirmStructureImport: ConfirmStructureImport;
  readonly createEmployee: CreateEmployee;
  readonly updateEmployee: UpdateEmployee;
  readonly changeEmployeeStatus: ChangeEmployeeStatus;
  readonly createTeam: CreateTeam;
  readonly moveEmployeeToTeam: MoveEmployeeToTeam;
  readonly createExternalService: CreateExternalService;
  readonly updateExternalService: UpdateExternalService;
  readonly updateTeam: UpdateTeam;
  readonly getWorkCalendar: GetWorkCalendar;
  readonly getSystemCalendar: GetSystemCalendar;
  readonly addCalendarException: AddCalendarException;
  readonly importCalendarExceptions: ImportCalendarExceptions;
  readonly removeCalendarException: RemoveCalendarException;
  readonly getResourceCapacity: GetResourceCapacity;
  readonly addActivityItem: AddActivityItem;
  readonly updateActivityItem: UpdateActivityItem;

  readonly getWeeklyBoard: GetWeeklyBoard;
  readonly getResourceAvailability: GetResourceAvailability;
  readonly suggestWeeklyDistribution: SuggestWeeklyDistribution;
  readonly getOrCreateWeeklyPlan: GetOrCreateWeeklyPlan;
  readonly createWeeklyAllocation: CreateWeeklyAllocation;
  readonly updateWeeklyAllocation: UpdateWeeklyAllocation;
  readonly deleteWeeklyAllocation: DeleteWeeklyAllocation;
  readonly changeWeeklyPlanStatus: ChangeWeeklyPlanStatus;
  readonly suggestScheduleAllocation: SuggestScheduleAllocation;
  readonly applyScheduleAllocation: ApplyScheduleAllocation;
  readonly getDailySchedule: GetDailySchedule;
  readonly moveAllocationToDay: MoveAllocationToDay;
  readonly getDailyInspections: GetDailyInspections;
  readonly adjustInspectionAllocation: AdjustInspectionAllocation;
  readonly removeInspectionAllocation: RemoveInspectionAllocation;
  readonly confirmInspection: ConfirmInspection;
  readonly registerInspectionAbsence: RegisterInspectionAbsence;
  readonly reopenInspection: ReopenInspection;
  readonly getDailyReport: GetDailyReport;
  readonly getOrOpenDailyReport: GetOrOpenDailyReport;
  readonly closeDailyReport: CloseDailyReport;
  readonly reviseDailyReport: ReviseDailyReport;
  readonly setDailyReportWeather: SetDailyReportWeather;
  readonly getDailyReportTasks: GetDailyReportTasks;
  readonly setDailyReportTasks: SetDailyReportTasks;
  readonly setDailyReportNotes: SetDailyReportNotes;
  readonly getDailyReportCrew: GetDailyReportCrew;
  readonly setDailyReportCrew: SetDailyReportCrew;
  readonly setDailyReportEquipment: SetDailyReportEquipment;
  readonly getPlanningChangeHistory: GetPlanningChangeHistory;
  readonly openInspection: OpenInspection;
  readonly recordDayMeasurement: RecordDayMeasurement;
  readonly getDayTeamMeasurements: GetDayTeamMeasurements;
  readonly recordTeamMeasurement: RecordTeamMeasurement;
  readonly recordReportedTeamWork: RecordReportedTeamWork;
  readonly createTeamAllocation: CreateTeamAllocation;
  readonly recordReportedWork: RecordReportedWork;
  readonly attachInspectionFile: AttachInspectionFile;
  readonly listInspectionAttachments: ListInspectionAttachments;
  readonly attachDailyReportPhoto: AttachDailyReportPhoto;
  readonly listDailyReportPhotos: ListDailyReportPhotos;
  readonly attachNonConformityPhoto: AttachNonConformityPhoto;
  readonly listNonConformityPhotos: ListNonConformityPhotos;
  readonly attachTeamMeasurementPhoto: AttachTeamMeasurementPhoto;
  readonly listTeamMeasurementPhotos: ListTeamMeasurementPhotos;
  readonly removeInspectionAttachment: RemoveInspectionAttachment;
  readonly getAttachmentUrl: GetAttachmentUrl;
  readonly copyPreviousWeek: CopyPreviousWeek;

  readonly getStorageStatus: GetStorageStatus;
  readonly requestStoragePersistence: RequestStoragePersistence;
  readonly runIntegrityCheck: RunIntegrityCheck;
  readonly repairIntegrity: RepairIntegrity;
  readonly getSupplyDigest: GetSupplyDigest;
  readonly acknowledgeSupplyMaterial: AcknowledgeSupplyMaterial;
  readonly listProjectRequisitions: ListProjectRequisitions;

  readonly signInWithPassword: SignInWithPassword;
  readonly setPassword: SetPassword;
  readonly signOut: SignOut;
  readonly revokeUserSessions: RevokeUserSessions;
  /** As chaves de API (07/10/2026). */
  readonly createApiKey: CreateApiKey;
  readonly listApiKeys: ListApiKeys;
  readonly revokeApiKey: RevokeApiKey;
  readonly getSessionToken: GetSessionToken;
  readonly setSessionToken: SetSessionToken;
  readonly clearSessionToken: ClearSessionToken;

  readonly permissions: PermissionChecker;
  /** Exposto para que nem a interface precise chamar `new Date()` (RN-123). */
  readonly clock: Clock;
}

/**
 * O que o aparelho resolve sozinho, sem a rede.
 *
 * | Fica no aparelho      | Por quê                                               |
 * | --------------------- | ----------------------------------------------------- |
 * | obra aberta           | Estado da tela. No servidor, trocaria a obra do outro |
 * | o segredo da sessão   | É do aparelho; como endpoint, entregaria a chave      |
 * | cota de armazenamento | Do navegador; não existe no servidor                  |
 * | permissões e relógio  | A tela decide o que mostrar sem perguntar à rede      |
 */
export type DeviceContainer = Pick<
  Container,
  | 'bootstrap'
  | 'getSessionToken'
  | 'setSessionToken'
  | 'clearSessionToken'
  | 'getActiveProjectId'
  | 'setActiveProject'
  | 'getStorageStatus'
  | 'requestStoragePersistence'
  | 'permissions'
  | 'clock'
>;

export interface ContainerOptions {
  /** Banco alternativo — o teste da versão 35 abre um com nome próprio. */
  readonly db?: ObraPlanDb;
  /** Relógio congelável, para testar regras que dependem de "hoje" (RN-123). */
  readonly clock?: Clock;
  /** Inspetor de armazenamento alternativo: fora do navegador não há `navigator.storage`. */
  readonly storageInspector?: StorageInspector;
}

export function createDeviceContainer(options: ContainerOptions = {}): DeviceContainer {
  const db = options.db ?? getDb();
  const clock = options.clock ?? new SystemClock();
  const settings = new DexieSettingsRepository(db, clock);
  const storageInspector = options.storageInspector ?? new BrowserStorageInspector();
  const permissions = new PermissionChecker();

  return {
    // Abrir o banco é o que sobe a versão (e apaga as tabelas de operação na primeira vez).
    // Falha aqui — navegador que bloqueia o armazenamento — é a que a tela de entrada mostra.
    bootstrap: async () => {
      await db.open();
    },
    getSessionToken: new GetSessionToken(settings),
    setSessionToken: new SetSessionToken(settings),
    clearSessionToken: new ClearSessionToken(settings),
    getActiveProjectId: new GetActiveProjectId(settings),
    setActiveProject: new SetActiveProject(settings, permissions),
    getStorageStatus: new GetStorageStatus(storageInspector),
    requestStoragePersistence: new RequestStoragePersistence(storageInspector),
    permissions,
    clock,
  };
}
